import { useMemo } from "react";
import { Navigate, Outlet, useSearchParams } from "react-router-dom";
import { useFetchPatient } from "@/infra/apis/patients/useFetchPatient";
import { useFetchActiveTreatment } from "@/infra/apis/treatments/useFetchActiveTreatment";
import { Exceptions, useAuth } from "@akina/infra";
import { LoadingOverlay } from "@akina/presentation";
import { AppRoutes } from ".";
import { LOCALE_GET_PARAM } from "./utils";

const ProtectedRoute = () => {
  const { isAuthenticated, isLoading: isAuthLoading, user } = useAuth();
  const {
    data: patient,
    isLoading: isPatientLoading,
    error: patientError,
  } = useFetchPatient({
    throwOnError: false,
    isDeepLinkHandled: true,
  });
  const { isLoading: isActiveTreatmentLoading, error: activeTreatmentError } = useFetchActiveTreatment(true);
  const isLoading = isAuthLoading || isPatientLoading || isActiveTreatmentLoading;
  const [searchParams] = useSearchParams();
  const locale = searchParams.get(LOCALE_GET_PARAM);
  const search = locale ? `?${LOCALE_GET_PARAM}=${locale}` : "";

  const isOnboardingVerificationPage = AppRoutes.OnboardingVerify === (location.pathname as AppRoutes);
  const isPrescriptionExpired = useMemo(
    () =>
      !isLoading &&
      (patientError instanceof Exceptions[404] ||
        patient?.state === "completed" ||
        patient?.state === "detached" ||
        patient?.state === "passive"),
    [patientError, patient, isLoading],
  );
  const isPatientDisconnected = useMemo(
    () => !isLoading && activeTreatmentError instanceof Exceptions[404],
    [activeTreatmentError, isLoading],
  );

  if (isLoading) {
    return <LoadingOverlay />;
  }
  if (isOnboardingVerificationPage) return <Outlet />;

  if (!isAuthenticated) return <Navigate to={{ pathname: AppRoutes.Onboarding, search }} />;
  if (!user?.email_verified) return <Navigate to={AppRoutes.OnboardingVerify} />;
  if (isPrescriptionExpired && (location.pathname as AppRoutes) !== AppRoutes.PrescriptionExpired)
    return <Navigate to={AppRoutes.PrescriptionExpired} replace />;
  if (
    !isPrescriptionExpired && // check needed to avoid infinite loops if both guards are true
    isPatientDisconnected &&
    (location.pathname as AppRoutes) !== AppRoutes.PatientDisconnected
  )
    return <Navigate to={AppRoutes.PatientDisconnected} replace />;

  return <Outlet />;
};

export default ProtectedRoute;
